import Color from "color";
import type { Theme } from "@/lib/theme/type";
import { rgb } from "@/utils/color";
import type { CssVars } from "@/utils/css-vars";

export function convertThemeToCssVars(theme: Theme): CssVars {
  return reduce(theme);
}

/** Ionic's step ladder: 5% to 95% of the way from the background to the text colour. */
const ION_STEPS = Array.from({ length: 19 }, (_, index) => (index + 1) * 50);

/**
 * The greys Ionic mixes its own components out of.
 *
 * `variables.css` hands Ionic our colours, but only the ones it names — `--ion-background-color`,
 * `--ion-text-color` and the palette. Everything in between, Ionic expects to find in a step
 * ladder: the handle on a sheet modal, the divider under a searchbar, the placeholder in an input,
 * the text in an `ion-note`. Where that ladder is missing, the components fall back to hexes baked
 * into the framework — `#c0c0be` for that sheet handle — which are light-mode greys and stay light
 * grey on a dark screen.
 *
 * Ionic ships a dark palette that defines these, but taking it would mean taking its background and
 * text colours too, at a specificity that beats ours: every brand would get Ionic's night instead
 * of its own. So the ladder is mixed from the theme the app actually resolved, which covers both
 * appearances and every brand from one function.
 */
export function convertThemeToIonicStepCssVars(theme: Theme): CssVars {
  const background = parse(theme.color?.surface?.background) ?? Color("#ffffff");
  const text = parse(theme.color?.txt?.primary) ?? Color("#000000");

  return ION_STEPS.reduce<CssVars>((cssVars, step) => {
    const ratio = step / 1000;
    const fromBackground = background.mix(text, ratio).hex().toLowerCase();

    // The legacy name is what most components reach for first, with the two directional names as
    // its fallbacks — so all three have to agree or a component picks up a stale grey.
    cssVars[`--ion-color-step-${step}`] = fromBackground;
    cssVars[`--ion-background-color-step-${step}`] = fromBackground;
    cssVars[`--ion-text-color-step-${step}`] = text.mix(background, ratio).hex().toLowerCase();

    return cssVars;
  }, {});
}

/** The theme comes off a free-form JSON endpoint; a step that is not a colour may not throw. */
function parse(color: unknown): Color | undefined {
  if (typeof color !== "string" || color.trim() === "") {
    return undefined;
  }

  try {
    return Color(color);
  } catch {
    return undefined;
  }
}

function reduce(object: object, acc?: CssVars, previousKey?: string): CssVars {
  return Object.entries(object).reduce<CssVars>(
    (acc, [key, value]) => perform(acc, [key, value], previousKey),
    acc ?? {},
  );
}

function perform(acc: CssVars, [key, value]: [string, unknown], previousKey?: string): CssVars {
  const currentKey = [previousKey, key].filter(Boolean).join("-");

  if (typeof value === "string") {
    acc[`--${currentKey}`] = value;
    return acc;
  }

  if (typeof value === "object") {
    return reduce(value as object, acc, currentKey);
  }

  return acc;
}

export function appendRgbToCssVars(cssVars: CssVars): CssVars<`--${string}` | `--${string}-rgb`> {
  return Object.entries(cssVars).reduce(
    (acc, [key, value]) => {
      const typedKey = key as `--${string}`;
      acc[typedKey] = value;
      acc[`${typedKey}-rgb`] = rgb(value);
      return acc;
    },
    {} as CssVars<`--${string}` | `--${string}-rgb`>,
  );
}
